import { Schema } from 'prosemirror-model'

// A ProseMirror schema scoped to exactly what a tldraw text box needs — deliberately NOT prose-core's
// shared doc schema. Two scoping choices fix whole classes of bugs by construction:
//
//   • `doc` is `block+` (not prose-core's `block* textWrap`), so a list can be the LAST node. There is
//     no schema-forced trailing paragraph, so no phantom blank line and nothing for forward-Delete to
//     pull into a list (the ProseInput editor avoids the same bugs the same way).
//   • `list_item` is `paragraph` (a single paragraph, not `block+`), so a list item can never hold a
//     stray empty paragraph beside its content — the `li > [p, p]` strand is structurally impossible.
//
// Node and mark NAMES + ATTRS match prose-core (depth/level/counterStyles/color/weight/…) so the stored
// doc JSON and the monospace renderer's walk are unchanged; only the content rules differ. The monospace
// renderer dupes this schema (see monospace's textboxSchema) to parse the same JSON.
const ORDERED_LIST_COUNTER_STYLES = ['decimal', 'lower-alpha', 'lower-roman']
const DEFAULT_FONT_WEIGHT = 400

const depthAttr = (dom: HTMLElement): number =>
  dom.hasAttribute('data-depth') ? parseInt(dom.getAttribute('data-depth') ?? '0', 10) || 0 : 0

export const textboxSchema = new Schema({
  nodes: {
    doc: { content: 'block+' },
    paragraph: {
      content: 'inline*',
      group: 'block',
      // `alignment` is kept only so old prose-core docs round-trip; box alignment is a shape prop.
      attrs: { alignment: { default: null }, depth: { default: 0 } },
      parseDOM: [
        {
          tag: 'p',
          getAttrs: (dom) => ({
            alignment: (dom as HTMLElement).getAttribute('data-alignment'),
            depth: depthAttr(dom as HTMLElement),
          }),
        },
      ],
      toDOM: (node) => [
        'p',
        { 'data-alignment': node.attrs.alignment, 'data-depth': (node.attrs.depth ?? 0).toString() },
        0,
      ],
    },
    unordered_list: {
      content: 'list_item+',
      group: 'block',
      attrs: { markerFormat: { default: 'circle' } },
      parseDOM: [{ tag: 'ul' }],
      toDOM: (node) => ['ul', { 'data-markerformat': node.attrs.markerFormat, 'data-type': 'unordered_list' }, 0],
    },
    ordered_list: {
      content: 'list_item+',
      group: 'block',
      attrs: {
        counterSeparator: { default: '.' },
        counterStyles: { default: ORDERED_LIST_COUNTER_STYLES },
        flattenMarker: { default: true },
        listStartOffset: { default: 1 },
      },
      parseDOM: [
        {
          tag: 'ol',
          getAttrs: (dom) => {
            const el = dom as HTMLElement
            const styles = el.getAttribute('data-counterstyles')
            const start = el.getAttribute('data-liststartoffset')
            return {
              counterSeparator: el.getAttribute('data-counterseparator') ?? '.',
              counterStyles: styles ? styles.split(',') : ORDERED_LIST_COUNTER_STYLES,
              flattenMarker: el.getAttribute('data-flattenmarker') === 'true',
              listStartOffset: start ? parseInt(start, 10) || 1 : 1,
            }
          },
        },
      ],
      toDOM: (node) => [
        'ol',
        {
          'data-flattenmarker': (node.attrs.flattenMarker ?? true).toString(),
          'data-counterseparator': node.attrs.counterSeparator,
          'data-counterstyles': (node.attrs.counterStyles ?? ORDERED_LIST_COUNTER_STYLES).toString(),
          'data-liststartoffset': (node.attrs.listStartOffset ?? 1).toString(),
        },
        0,
      ],
    },
    list_item: {
      // A SINGLE paragraph — never `block+` — so an empty paragraph can't be stranded beside content.
      content: 'paragraph',
      attrs: { depth: { default: 0 } },
      parseDOM: [{ tag: 'li', getAttrs: (dom) => ({ depth: depthAttr(dom as HTMLElement) }) }],
      toDOM: (node) => ['li', { 'data-depth': (node.attrs.depth ?? 0).toString() }, 0],
    },
    text: { group: 'inline' },
  },
  // Order matters for nesting/precedence: colour first so underline/strike inherit it; strong after
  // font_weight so bold wins. Only the marks the toolbar applies and the renderer reads are included.
  marks: {
    colored: {
      attrs: { color: { default: null } },
      parseDOM: [
        { tag: 'span[data-color]', getAttrs: (dom) => ({ color: (dom as HTMLElement).getAttribute('data-color') }) },
      ],
      toDOM: (mark) => ['span', { 'data-color': mark.attrs.color, style: `color: ${mark.attrs.color}` }, 0],
    },
    em: { parseDOM: [{ tag: 'i' }, { tag: 'em' }], toDOM: () => ['em', 0] },
    strike: { parseDOM: [{ tag: 'strike' }, { tag: 'del' }], toDOM: () => ['del', 0] },
    underline: { parseDOM: [{ tag: 'u' }], toDOM: () => ['u', 0] },
    font_weight: {
      attrs: { weight: { default: DEFAULT_FONT_WEIGHT } },
      parseDOM: [
        {
          style: 'font-weight',
          getAttrs: (value) => {
            const weight = parseInt(value as string, 10)
            return weight ? { weight } : false
          },
        },
      ],
      toDOM: (mark) => ['span', { style: `font-weight: ${mark.attrs.weight}` }, 0],
    },
    strong: { parseDOM: [{ tag: 'strong' }, { tag: 'b' }], toDOM: () => ['strong', 0] },
  },
})

export const MAX_LIST_DEPTH = 10
